import { isAdmin } from '@ofmos/shared/auth';
import type { ReactNode } from 'react';
import { EmptyState, Spinner } from '../../ui';
import { useSession } from './useSession';

/**
 * The admin route wrapper (admin-area.md D2), beside `RequireAuth`.
 *
 * ONE PLACE, not six in-component branches. Before this, role enforcement in the browser
 * was inconsistent three ways: the sidebar hid the links, two pages rendered an
 * "Admins only" empty state inside themselves, and the object, field and webhook screens
 * had no client guard at all - so a member who typed the URL got the full editor and
 * discovered it was read-only by watching saves fail with a 403.
 *
 * THE BROWSER GUARD IS A COURTESY, NEVER THE WALL. Every mutation behind it is
 * `requireAdmin` on the server and that stays the enforcement; this exists so a member
 * is told, rather than shown a form that will refuse them.
 *
 * `/settings/objects` is deliberately NOT wrapped: D2 keeps the data model readable by
 * members as an explicit READ-ONLY view, because hiding it would hide the only place the
 * meaning of a field is written down. That screen disables its own controls instead.
 */
export function RequireAdmin({ children }: { readonly children: ReactNode }) {
  const session = useSession();

  if (session.isPending) {
    return (
      <div className="flex min-h-64 items-center justify-center py-12" aria-busy="true">
        <Spinner label="Checking your access" />
      </div>
    );
  }

  const user = session.data;
  if (user === null || user === undefined || !isAdmin(user.role)) {
    return (
      <EmptyState
        icon="blocked"
        title="Admins only"
        description="This part of Settings changes how the whole instance behaves, so it is limited to administrators. Ask an admin if you need something here."
      />
    );
  }

  return children;
}
